/**
 * React Query Client Configuration
 *
 * Centralizes query client settings for the application.
 * Configured to match previous Zustand cache behavior (5-minute TTL).
 */

import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Match previous 5-minute cache TTL from Zustand slices
      staleTime: 5 * 60 * 1000,
      // Keep data in cache for 10 minutes after becoming unused
      gcTime: 10 * 60 * 1000,
      // Retry failed requests up to 2 times
      retry: 2,
      // Don't refetch on window focus by default (SSE handles updates)
      refetchOnWindowFocus: false,
      // Don't refetch on reconnect (SSE handles this)
      refetchOnReconnect: false,
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
    },
  },
});

/**
 * Query key factories for type-safe, consistent cache keys
 */
export const postKeys = {
  all: (clientId: string) => ["posts", clientId] as const,
  byStatus: (clientId: string, status: string) =>
    [...postKeys.all(clientId), "status", status] as const,
  byStage: (clientId: string, stage: string) =>
    [...postKeys.all(clientId), "stage", stage] as const,
  single: (clientId: string, postId: string) =>
    [...postKeys.all(clientId), "single", postId] as const,
};

export const mediaKeys = {
  all: (clientId: string) => ["media", clientId] as const,
  byContentType: (clientId: string, contentType: string) =>
    [...mediaKeys.all(clientId), "type", contentType] as const,
  byStage: (clientId: string, stage: string) =>
    [...mediaKeys.all(clientId), "stage", stage] as const,
  filtered: (clientId: string, filters: Record<string, unknown>) =>
    [...mediaKeys.all(clientId), "filtered", filters] as const,
  single: (clientId: string, mediaId: number) =>
    [...mediaKeys.all(clientId), "single", mediaId] as const,
};

export const clientKeys = {
  all: () => ["clients"] as const,
  single: (clientId: string) => [...clientKeys.all(), clientId] as const,
};
